import { useIsMobile } from "../utils/viewport";
import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import {
  Send, Paperclip, X, Loader2, ChevronLeft, RefreshCw, Download, ChevronDown,
  Briefcase, Gauge, Landmark, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { LogoMark } from "./Logo";
import * as api from "../utils/api";
import AssistantText from "./AssistantText";
import { AllMethodologiesResult, BrokerCalcResult, BrokerOffer } from "../types";
import { narrate, narrateBroker } from "../utils/agentNarrative";
import {
  DeterministicAgent, initialContext, AgentContext, AgentMode, AgentAction,
  DEFAULT_LIMIT, extractTenderNumber, isClientAnalyticsIntent,
} from "../utils/agentEngine";
import { parseRiskScore, formatRubles } from "../utils/math_helpers";
import { useSnsFlow, ChatAction, SnsBlock } from "../utils/useSnsFlow";
import SnsOffersBlock from "./SnsOffersBlock";
import { guaranteeAmountFor, lawLabel, normLaw } from "../utils/law";

import ANYskinTab from "./ANYskinTab";
import CreditTab from "./CreditTab";
import SolvencyTab from "./SolvencyTab";
import ScoreTab from "./ScoreTab";
import CompareTab from "./CompareTab";
import FinancesTab from "./FinancesTab";
import AdviceBlock from "./AdviceBlock";
import TargetReportCard from "./TargetReportCard";

// Вкладка Крылова — отдельным чанком: AiAgent входит в главный бандл (бюджет 85 КБ).
const KrylovTab = lazy(() => import("./KrylovTab"));

const ACCEPT = ".csv,.xlsx,.pdf,.png,.jpg,.jpeg,.tiff,.webp";
const SCAN_RE = /\.(pdf|png|jpe?g|tiff?|webp)$/i;

type Msg =
  | { id: number; role: "assistant" | "user"; kind: "text"; text: string; files?: string[] }
  | { id: number; role: "assistant"; kind: "result"; text: string; result: AllMethodologiesResult; amount: number }
  | { id: number; role: "assistant"; kind: "broker"; text: string; broker: BrokerCalcResult; sns?: SnsBlock }
  // Сообщение с кнопками (заявка в СНС, #440); после нажатия кнопки гаснут.
  | { id: number; role: "assistant"; kind: "actions"; text: string; actions: ChatAction[]; done?: boolean }
  // Выбор банков СНС чекбоксами (#446).
  | { id: number; role: "assistant"; kind: "snspick"; text: string; offers: BrokerOffer[] }
  // Ответ ЛЛМ-ассистента (нестандартный запрос): текст + оценка 👍/👎 в базу принятых.
  | { id: number; role: "assistant"; kind: "assistant"; text: string; question: string;
      toolCalls?: api.AssistantToolCall[]; ctx?: Record<string, any>; accepted?: "up" | "down" | null };

let _seq = 0;
const nid = () => ++_seq;

type BrokerAct = Extract<AgentAction, { type: "broker" }>;

// Подпись значимых полей контекста: изменилась → пользователь дал параметр формы
// (сумма/срок/ИНН/закон…), а не задал вопрос. Незначимая мутация ctx не в счёт.
function ctxSig(c: AgentContext): string {
  return JSON.stringify([
    c.inn, c.amount, c.days, c.law, c.guaranteeType, c.category, c.beneficiaryInn,
    c.okpd2, c.tenderNumber, c.experienceDeclared, c.sort, c.limit, c.sourceFilter,
    c.eligibleOnly, (c.excludeBanks || []).join(","), JSON.stringify(c.flags || {}),
    JSON.stringify(c.principal || {}),
  ]);
}

// Сравнители сортировки результата — те же семантики, что client-side в разделе «Банки».
const SORT_CMP: Record<string, (a: BrokerOffer, b: BrokerOffer) => number> = {
  best: (a, b) => (b.avRate || 0) - (a.avRate || 0) || (a.commission ?? Infinity) - (b.commission ?? Infinity),
  expensive: (a, b) => (b.commission ?? -Infinity) - (a.commission ?? -Infinity),
  cheap: (a, b) => (a.commission ?? Infinity) - (b.commission ?? Infinity),
  kv: (a, b) => (b.avRate || 0) - (a.avRate || 0),
};

// «Вид» результата: фильтр по источнику/проходимости → сортировка → топ-N. Всё поверх
// существующих полей оффера — никаких новых расчётов (как фильтры в разделе «Банки»).
function brokerView(res: BrokerCalcResult, p: {
  sort?: string | null; limit?: number | null; sourceFilter?: string | null; eligibleOnly?: boolean; excludeBanks?: string[];
}): BrokerOffer[] {
  let arr = [...(res.offers || [])];
  if (p.sourceFilter) arr = arr.filter((o) => o.source === p.sourceFilter);
  if (p.eligibleOnly) arr = arr.filter((o) => o.eligible);
  if (p.excludeBanks && p.excludeBanks.length)
    arr = arr.filter((o) => !p.excludeBanks!.some((s) => (o.bank || "").toLowerCase().includes(s)));
  const cmp = SORT_CMP[p.sort || "best"] || SORT_CMP.best;
  if (!p.sort || p.sort === "best")
    arr.sort((a, b) => (Number(!a.eligible) - Number(!b.eligible)) || cmp(a, b));  // проходящие выше
  else
    arr.sort(cmp);   // явный запрос («5 самых дорогих» и т.п.) — строго по ключу
  if (p.limit) arr = arr.slice(0, p.limit);
  return arr;
}

// Подпись параметров поиска (без предпочтений показа) — для ре-сортировки без повторного запроса.
function brokerSig(a: BrokerAct): string {
  return JSON.stringify({
    amount: a.amount, days: a.days, category: a.category, guaranteeType: a.guaranteeType,
    law: a.law, inn: a.inn, beneficiaryInn: a.beneficiaryInn, okpd2: a.okpd2,
    experienceDeclared: a.experienceDeclared, principal: a.principal, flags: a.flags, tenderNumber: a.tenderNumber,
  });
}

// Тело запроса к существующему /api/broker/calc из распознанных параметров.
function brokerReq(a: BrokerAct, applicant?: Record<string, any>): Record<string, any> {
  return {
    category: a.category, guaranteeType: a.guaranteeType || undefined, law: a.law || undefined,
    amount: a.amount, days: a.days, flags: a.flags, inn: a.inn || undefined,
    beneficiaryInn: a.beneficiaryInn || undefined, okpd2: a.okpd2 || undefined,
    tenderNumber: a.tenderNumber || undefined, experienceDeclared: a.experienceDeclared ?? undefined,
    principal: a.principal || {}, ...(applicant ? { applicant } : {}),
  };
}

// Экспорт CSV — те же колонки, что и в основном отчёте (App.handleExportCsv).
async function exportCsv(r: AllMethodologiesResult) {
  const { krylovCsvRows } = await import("../utils/krylov");
  const rows = [
    ["Параметр", "Значение"],
    ["ИНН", r.company.inn],
    ["Полное наименование", r.company.fullName],
    ["Статус", r.company.status],
    ["ANYskin: рейтинг", r.anyskin.rating],
    ["ANYskin: итог", String(r.anyskin.totalScore)],
    ["Credit: F-score", String(r.credit.fScore)],
    ["Credit: риск", r.credit.riskCategory],
    ["Solvency: интерпретация", r.solvency.interpretation],
    ["Score: баллов", `${r.score.totalPoints} (${r.score.scoreClass})`],
    ["Score: TOP банк", `${r.score.sberbankScore} (${r.score.sberbankClass})`],
    ...krylovCsvRows(r),
  ];
  const csv = "﻿" + rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  link.download = `anyskin_${r.company.inn}_${Date.now()}.csv`;
  link.click();
}

function Section({ title, children, open }: { title: string; children: React.ReactNode; open?: boolean }) {
  return (
    <details open={open} className="group bg-white border border-slate-100 rounded-xl overflow-hidden">
      <summary className="flex items-center justify-between gap-2 px-4 py-2.5 cursor-pointer select-none text-xs font-bold text-slate-700 hover:bg-slate-50">
        {title}
        <ChevronDown className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="p-3 sm:p-4 border-t border-slate-100 overflow-x-auto">{children}</div>
    </details>
  );
}

// Сворачиваемый блок «материалов» (текст в пузыре всегда виден, это — раскрывается).
// Именованная группа group/col — чтобы не конфликтовать с group у Section внутри.
function Collapse({ title, children, defaultOpen }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  return (
    <details open={defaultOpen} className="group/col bg-white border border-slate-100 rounded-xl overflow-hidden">
      <summary className="flex items-center justify-between gap-2 px-4 py-2.5 cursor-pointer select-none text-xs font-bold text-slate-700 hover:bg-slate-50">
        {title}
        <ChevronDown className="w-4 h-4 text-slate-400 transition-transform group-open/col:rotate-180" />
      </summary>
      <div className="p-3 sm:p-4 border-t border-slate-100 space-y-2">{children}</div>
    </details>
  );
}

function ResultBubble({ m, onRecalc, recalcBusy, onAskAssistant }: {
  m: Extract<Msg, { kind: "result" }>;
  onRecalc?: (m: Extract<Msg, { kind: "result" }>) => void;
  recalcBusy?: boolean;
  onAskAssistant?: (question: string) => void;
}) {
  const r = m.result;
  const sb = parseRiskScore(r.company.riskCheckScore);
  const chips: [string, string][] = [
    ["Рейтинг ANYskin", r.anyskin.rating],
    ["Кредит-риск", r.credit.riskCategory],
    ["100 баллов", `${r.score.totalPoints} · ${r.score.scoreClass}`],
    ["Индекс СЭБ", sb.short],
  ];
  return (
    <div className="space-y-3">
      {r.projected && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-bold rounded-lg px-3 py-1.5 inline-block">
          📈 Проекция по рекомендованному балансу
        </div>
      )}
      <AssistantText text={m.text} className="text-sm text-slate-700 leading-relaxed" />
      <div className="flex flex-wrap gap-2">
        {chips.map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-1 text-[11px]">
            <span className="text-slate-500">{k}:</span>
            <b className="text-indigo-800">{v}</b>
          </span>
        ))}
        <button onClick={() => exportCsv(r)}
          className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg px-2.5 py-1 text-[11px] font-bold text-slate-700 cursor-pointer">
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
      </div>

      {/* Материалы (рекомендации, целевой баланс, таблицы методик) — по раскрытию;
          текст-сводка выше виден всегда. */}
      <Collapse title="📋 Детали: рекомендации, целевой баланс и таблицы методик">
        {r.recommendations && r.recommendations.length > 0 && (
          <AdviceBlock title="Что улучшить — сводный план" recommendations={r.recommendations} onAskAssistant={onAskAssistant} />
        )}
        {r.targetReport && (
          <TargetReportCard report={r.targetReport}
            onRecalc={onRecalc ? () => onRecalc(m) : undefined} recalcBusy={recalcBusy} />
        )}
        <Section title="Сравнение методик"><CompareTab data={r} onExportCsv={() => exportCsv(r)} /></Section>
        <Section title="ANYskin — рейтинг (G1/G2/USL)"><ANYskinTab data={r.anyskin} onAskAssistant={onAskAssistant} /></Section>
        <Section title="Кредитоспособность (K1–K7)"><CreditTab data={r.credit} onAskAssistant={onAskAssistant} /></Section>
        <Section title="Платёжеспособность (Л1–Л9)"><SolvencyTab data={r.solvency} onAskAssistant={onAskAssistant} /></Section>
        <Section title="100-балльная оценка СЭБ Anyskin"><ScoreTab data={r.score} onAskAssistant={onAskAssistant} /></Section>
        {r.krylov?.calculated && (
          <Section title="Углублённый анализ отчётности">
            <Suspense fallback={<p className="text-xs text-slate-400">Загрузка…</p>}><KrylovTab data={r.krylov} onAskAssistant={onAskAssistant} /></Suspense>
          </Section>
        )}
        <Section title="Финансовые показатели"><FinancesTab data={r.finances} /></Section>
      </Collapse>
    </div>
  );
}

// Диапазон суммы банка для пузыря брокера.
function amountRange(o: BrokerOffer): string {
  const f = (v?: number | null) => formatRubles(v || 0);
  if (o.amountMin && o.amountMax) return `${f(o.amountMin)} – ${f(o.amountMax)}`;
  if (o.amountMax) return `до ${f(o.amountMax)}`;
  if (o.amountMin) return `от ${f(o.amountMin)}`;
  return "—";
}

function BrokerResultBubble({ m, onSubmit, onSubmitMany, disabled }: {
  m: Extract<Msg, { kind: "broker" }>; onSubmit: (o: BrokerOffer) => void; onSubmitMany: (offers: BrokerOffer[]) => void; disabled?: boolean;
}) {
  // #441: банки, подключённые к СНС, — свёрнутым блоком после текста и первыми в карточках.
  const snsBanks = new Set((m.sns?.state === "ok" ? m.sns.offers : []).map((o) => `${o.bank}|${o.platform || ""}`));
  const offers = snsBanks.size
    ? [...m.broker.offers].sort((a, b) => Number(snsBanks.has(`${b.bank}|${b.platform || ""}`)) - Number(snsBanks.has(`${a.bank}|${a.platform || ""}`)))
    : m.broker.offers;
  return (
    <div className="space-y-3">
      <AssistantText text={m.text} className="text-sm text-slate-700 leading-relaxed" />
      {/* Банки СНС — свёрнутым предложением после текста ответа, перед карточками (#441). */}
      <SnsOffersBlock block={m.sns} onSubmit={onSubmit} onSubmitMany={onSubmitMany} disabled={disabled} collapsible />
      <Collapse title={`🏦 Предложения банков: ${m.broker.eligibleCount} из ${m.broker.count} — раскрыть`}>
        {offers.map((o, i) => (
          <div key={i} className={`border rounded-xl p-3 ${o.eligible ? "border-slate-100" : "border-slate-100 opacity-80"}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="font-bold text-sm text-slate-900 flex items-center gap-2 flex-wrap">
                <Landmark className="w-3.5 h-3.5 text-indigo-500 shrink-0" />{o.bank}
                {snsBanks.has(`${o.bank}|${o.platform || ""}`) && <span className="text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded">СНС</span>}
                {o.rating && <span className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded">{o.rating}</span>}
                {o.eligible
                  ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full"><CheckCircle2 className="w-3 h-3" /> Проходит</span>
                  : <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-full"><AlertTriangle className="w-3 h-3" /> Не проходит</span>}
              </div>
              {/* Подать можно только в банк, подключённый к СНС: в остальные заявка идёт мимо
                  платформы, и кнопка вела бы в форму, из которой банк не выбрать. */}
              {o.eligible && snsBanks.has(`${o.bank}|${o.platform || ""}`) && (
                <button onClick={() => onSubmit(o)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold rounded-lg cursor-pointer shrink-0">
                  <Send className="w-3 h-3" /> Подать заявку
                </button>
              )}
              {o.platform && <span className="text-[10px] text-slate-400 w-full">площадка: {o.platform}</span>}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
              <div><div className="text-slate-400">Комиссия</div><b className="font-mono text-slate-900">{formatRubles(o.commission)}</b></div>
              <div><div className="text-slate-400">КВ агента{o.avRate ? ` · ${Math.round(o.avRate * 100)}%` : ""}</div><b className="font-mono text-emerald-700">{formatRubles(o.av)}</b></div>
              <div><div className="text-slate-400">Лимит / срок</div><b className="text-slate-700">{amountRange(o)}{o.termMaxDays ? ` · до ${o.termMaxDays} дн.` : ""}</b></div>
            </div>
            {!o.eligible && o.reasons.length > 0 && (
              <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50/60 border border-amber-100 rounded-lg p-2">
                {o.reasons.map((r, j) => <div key={j}>• {r}</div>)}
              </div>
            )}
          </div>
        ))}
      </Collapse>
    </div>
  );
}

export default function AiAgent({ onBack, initialQuestion, initialResult, seedContext,
                                 seedProduct, onConsumedInitial, floating }: {
  onBack: () => void;
  // Вопрос из «Подробнее» (кнопка/значок ⓘ у рекомендаций и показателей методик) —
  // отправляется один раз при открытии, как если бы пользователь сам его набрал.
  initialQuestion?: string | null;
  // Уже посчитанный анализ (если «Подробнее» нажали из карточки клиента) — чтобы
  // ассистент отвечал по тем же цифрам, а не просил ИНН заново.
  initialResult?: AllMethodologiesResult | null;
  // Готовый контекст от вызывающего экрана (A16, #1189): «Спросить про эту заявку»
  // открывает окно, уже зная заявку, её банки и открытый допзапрос. Держится на весь
  // разговор, а не на первый вопрос: следующее «а если отказ?» — о той же заявке.
  seedContext?: Record<string, any> | null;
  // Подсказка маршрута инструментов (`apps` — заявки). Без неё навигатор выбирает по
  // тексту вопроса, и «чем этот банк лучше» уехало бы в справочник, потеряв заявку.
  seedProduct?: string;
  // Сброс initialQuestion у родителя — иначе повторный заход в агента переспросит то же.
  onConsumedInitial?: () => void;
  // В плавающем окне (как «Диалог по заявке» в Брокере, BrokerChat) вместо полного
  // экрана: сам компонент становится скролл-контейнером фиксированной высоты
  // родителя, без привязки к высоте вьюпорта.
  floating?: boolean;
}) {
  const mobile = useIsMobile();
  const agent = useRef(new DeterministicAgent());
  const [mode, setMode] = useState<AgentMode>("analysis");
  const ctx = useRef<AgentContext>(initialContext("analysis"));
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [staged, setStaged] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);        // ЛЛМ отвечает на нестандартный запрос
  const [ocrPending, setOcrPending] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  // Последний подбор (результат + подпись параметров) — чтобы менять сортировку/лимит/
  // фильтр показа без повторного запроса к серверу.
  const lastBrokerRef = useRef<{ res: BrokerCalcResult; sig: string } | null>(null);
  // Последний экспресс-анализ — уходит в контекст ассистента (режим «Анализ»), чтобы
  // модель отвечала по уже показанным цифрам, а не считала заново.
  const lastAnalysisRef = useRef<AllMethodologiesResult | null>(null);

  useEffect(() => {
    setMessages([{ id: nid(), role: "assistant", kind: "text", text: agent.current.greeting(mode) }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // на маунте режим = "analysis"; переключение делает switchMode

  // Результат анализа — обновляем контекст при каждом изменении (не только на
  // маунте): окно плавающее и не закрывается между кликами «Подробнее», а экран
  // мог успеть пересчитаться.
  useEffect(() => {
    if (!initialResult) return;
    lastAnalysisRef.current = initialResult;
    if (initialResult.company?.inn) ctx.current = { ...ctx.current, inn: initialResult.company.inn };
  }, [initialResult]);

  // «Подробнее» (из рекомендации/подсказки методики): готовый вопрос отправляется
  // НАПРЯМУЮ ассистенту (askAssistant), в обход детерминированного разбора
  // (agent.turn) — тот видит текст вроде «Объясни рекомендацию…» как команду
  // анализа и, не найдя ИНН в ctx.current, переспрашивает ИНН вместо ответа.
  // Зависимость от initialQuestion (не []) — иначе окно плавающее, не
  // размонтируется между кликами «Подробнее», и второй клик молча терялся:
  // родитель меняет проп на новый вопрос, но эффект с пустым списком
  // зависимостей уже не перезапускался (нужно было закрыть и открыть заново).
  useEffect(() => {
    if (!initialQuestion) return;
    pushText("user", initialQuestion);
    askAssistant(initialQuestion);
    onConsumedInitial?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion]);

  // Переключение режима: сбрасываем контекст и приветствие под новый режим.
  const switchMode = (next: AgentMode) => {
    if (next === mode || busy) return;
    setMode(next);
    ctx.current = initialContext(next);
    setStaged(null);
    setMessages([{ id: nid(), role: "assistant", kind: "text", text: agent.current.greeting(next) }]);
  };
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const pushText = (role: "assistant" | "user", text: string, files?: string[]) =>
    setMessages((m) => [...m, { id: nid(), role, kind: "text", text, files }]);

  // ── Заявка в банк через СНС (#440) — тот же сценарий, что в «Диалоге по заявке» ──
  const [busyNote, setBusyNote] = useState<string | null>(null);
  const pushActions = (text: string, actions: ChatAction[]) => {
    const id = nid();
    const wrap = actions.map((a) => ({ ...a, run: () => {
      setMessages((m) => m.map((x) => (x.id === id ? { ...x, done: true } as Msg : x)));
      a.run();
    } }));
    setMessages((m) => [...m, { id, role: "assistant", kind: "actions", text, actions: wrap }]);
  };
  const withBusy = async (note: string, fn: () => Promise<void>) => {
    setBusy(true); setBusyNote(note);
    try { await fn(); } finally { setBusy(false); setBusyNote(null); }
  };
  // Карточки выбора у «AI агента» нет (нет формы «Брокера») — создаём её из собственного
  // подбора в момент подачи; повторная подача по тому же клиенту — в ту же карточку.
  const caseRef = useRef<{ ref: string; inn: string; selected: Record<string, any>[] } | null>(null);
  const ensureCase = async (o: BrokerOffer): Promise<string> => {
    const c = ctx.current;
    if (!c.inn) throw new Error("нужен ИНН принципала (клиента) — укажите его в диалоге, и я заведу заявку");
    const prev = caseRef.current && caseRef.current.inn === c.inn ? caseRef.current : null;
    const item = {
      bankId: o.bankId, inn: (o as any).inn, bank: o.bank, platform: o.platform, source: o.source, segment: o.segment,
      extIds: o.extIds, productMode: o.productMode, commission: o.commission, av: o.av, avRate: o.avRate,
      principalPrice: o.principalPrice, eligible: o.eligible, methodologyKey: o.methodologyKey, rating: o.rating,
    };
    const selected = [...(prev?.selected || []).filter((x) => !(x.bank === o.bank && x.platform === o.platform)), item];
    const rec = await api.saveBrokerSelection({
      seq: prev ? Number(prev.ref.split("-")[0]) : undefined, ref: prev?.ref,
      inn: c.inn, companyName: (lastBrokerRef.current?.res.principal as any)?.companyName,
      deal: { category: c.category ?? "gos", law: c.law, guaranteeType: c.guaranteeType,
              amount: c.amount || 0, days: c.days || 0, tenderNumber: c.tenderNumber || undefined,
              beneficiaryInn: c.beneficiaryInn || undefined, okpd2: c.okpd2 || undefined,
              flags: c.flags, experienceDeclared: c.experienceDeclared ?? undefined,
              principal: c.principal && Object.keys(c.principal).length ? c.principal : undefined },
      selected, status: "result",
    });
    caseRef.current = { ref: rec.ref, inn: c.inn, selected };
    return rec.ref;
  };
  const { snsOn, startSns, submitChosen, handleInFlow, handleIntents, fileLabel, resolveConnected } = useSnsFlow({
    pushText: (role, text) => pushText(role, text), pushActions, withBusy,
    pushSnsPicker: (text, offers) => setMessages((m) => [...m, { id: nid(), role: "assistant", kind: "snspick", text, offers }]),
    getOffers: () => lastBrokerRef.current?.res.offers || [],
    getDeal: () => ctx.current,
    getSaidTexts: () => messages.filter((m) => m.kind === "text" || m.kind === "assistant").map((m) => m.text),
    ensureCase,
  });

  const runAnalyze = async (inn: string, amount: number, files: File[]) => {
    setBusy(true);
    setOcrPending(files.some((f) => SCAN_RE.test(f.name)));
    try {
      const result = files.length
        ? await api.calculateWithReport(inn, amount, files[0])
        : await api.calculateAll(inn, amount);
      ctx.current.analyzed = true;
      ctx.current.files = [];
      lastAnalysisRef.current = result;
      setMessages((m) => [...m, { id: nid(), role: "assistant", kind: "result", text: narrate(result, amount), result, amount }]);
    } catch (e: any) {
      pushText("assistant", `Не удалось выполнить анализ: ${e?.message || e}. Проверьте, что это ИНН юрлица (10 цифр), и попробуйте снова.`);
    } finally {
      setBusy(false);
      setOcrPending(false);
    }
  };

  // Синтез действия «broker» из накопленного контекста (для пересчёта по документам).
  const actFromCtx = (c: AgentContext): BrokerAct => ({
    type: "broker", amount: c.amount!, days: c.days!, category: c.category ?? "gos",
    guaranteeType: c.guaranteeType, law: c.law, inn: c.inn, beneficiaryInn: c.beneficiaryInn,
    tenderNumber: c.tenderNumber, okpd2: c.okpd2, experienceDeclared: c.experienceDeclared,
    principal: c.principal, flags: c.flags, sort: c.sort, limit: c.limit,
    sourceFilter: c.sourceFilter, eligibleOnly: c.eligibleOnly, excludeBanks: c.excludeBanks,
  });

  // Подбор по распознанным параметрам + применение вида (сортировка/лимит/фильтр показа).
  const emitBroker = (res: BrokerCalcResult, a: BrokerAct, prefix = "") => {
    const view = brokerView(res, a);
    const id = nid();
    setMessages((m) => [...m, {
      id, role: "assistant", kind: "broker", broker: { ...res, offers: view }, sns: { state: "loading", offers: [] },
      text: prefix + narrateBroker({ ...res, offers: view }, a),
    }]);
    // #441: подключение к СНС — по ВСЕМ банкам подбора, первым списком в ответе.
    void resolveConnected(res.offers || []).then((block) =>
      setMessages((m) => m.map((x) => (x.id === id && x.kind === "broker" ? { ...x, sns: block } : x))));
  };

  const runBroker = async (a: BrokerAct) => {
    const sig = brokerSig(a);
    // Изменились только предпочтения показа → пересобираем вид без повторного запроса.
    if (lastBrokerRef.current && lastBrokerRef.current.sig === sig) {
      emitBroker(lastBrokerRef.current.res, a);
      return;
    }
    setBusy(true);
    try {
      const res = await api.brokerCalc(brokerReq(a));
      lastBrokerRef.current = { res, sig };
      emitBroker(res, a);
    } catch (e: any) {
      pushText("assistant", `Не удалось подобрать гарантию: ${e?.message || e}.`);
    } finally {
      setBusy(false);
    }
  };

  // Поиск по РНТ: подтягиваем тендер существующим lookup и заполняем пробелы (сумма
  // обеспечения по виду гарантии / закон / ИНН заказчика / ОКПД2). Срок из тендера не берём.
  const prefillFromTender = async (rnt: string) => {
    const c = ctx.current;
    try {
      const t = await api.brokerTenderLookup(rnt);
      c.tenderNumber = rnt;
      if (t.law && !c.law) { c.law = normLaw(t.law) || String(t.law); if (!c.category) c.category = "gos"; }
      const gtype = c.guaranteeType || "exec_no_adv";
      const gAmount = guaranteeAmountFor(t, gtype);   // своя сумма под вид БГ, без подмены и нулей
      if (gAmount != null && c.amount == null) c.amount = Math.round(gAmount * 100) / 100;
      if (t.customerInn && !c.beneficiaryInn) c.beneficiaryInn = t.customerInn;
      if (t.okpd2 && !c.okpd2) c.okpd2 = t.okpd2;
      pushText("assistant",
        `Подтянул тендер ${rnt}${t.customerName ? ` · ${t.customerName}` : ""}: ` +
        `${t.law ? `${lawLabel(t.law)}, ` : ""}${gAmount != null ? `обеспечение ${formatRubles(Math.round(gAmount))}` : "сумму обеспечения не нашёл — укажите вручную"}` +
        `${t.okpd2 ? `, ОКПД2 ${t.okpd2}` : ""}. Срок гарантии из тендера не берётся — укажите его («365 дней» / «1 год»).`);
    } catch (e: any) {
      pushText("assistant", `Не удалось найти тендер ${rnt}: ${e?.message || e}. Введите параметры вручную.`);
    }
  };

  // Пересчёт карточки по документам клиента: OCR отчётности (существующий /calculate-with-report)
  // → строки баланса → applicant.finances → повторный подбор по данным клиента.
  const runBrokerRecalcByDoc = async (file: File) => {
    const c = ctx.current;
    if (!c.inn) { pushText("assistant", "Чтобы пересчитать по документам клиента, укажите ИНН принципала (10 цифр) — по нему возьму базовые данные и наложу распознанную отчётность."); return; }
    if (c.amount == null || c.days == null) { pushText("assistant", "Сначала укажите сумму и срок гарантии — затем пересчитаю подбор по распознанной отчётности клиента."); return; }
    setBusy(true);
    setOcrPending(SCAN_RE.test(file.name));
    try {
      const rep = await api.calculateWithReport(c.inn, c.amount, file);
      const years = (rep.finances?.years as any[]) || [];
      const y = years.length ? years.reduce((p, q) => (Number(q?.year) || 0) > (Number(p?.year) || 0) ? q : p) : {};
      const finances: Record<string, number> = {};
      for (const k of Object.keys(y || {})) if (/^\d{4}$/.test(k) && typeof y[k] === "number") finances[k] = y[k];
      if (!Object.keys(finances).length) { pushText("assistant", "Не удалось распознать строки отчётности из документа. Нужна бухотчётность (баланс/ОФР) в PDF/Excel/скане."); return; }
      const a = actFromCtx(c);
      const res = await api.brokerCalc(brokerReq(a, { finances }));
      lastBrokerRef.current = null;   // база сменилась (по отчётности) — кэш ре-сорта сбрасываем
      emitBroker(res, a, `Пересчёт подбора по распознанной отчётности клиента (строк: ${Object.keys(finances).length}, источник — ваш документ «${file.name}»).\n\n`);
    } catch (e: any) {
      pushText("assistant", `Не удалось пересчитать по документам: ${e?.message || e}.`);
    } finally {
      setBusy(false);
      setOcrPending(false);
    }
  };

  const send = async (forced?: string) => {
    if (busy) return;
    const text = (forced ?? input).trim();
    const file = staged;
    if (!text && !file) return;
    pushText("user", text || (fileLabel(file) ? "(документ к заявке)" : "(прикреплён документ)"), file ? [file.name] : undefined);
    setInput("");
    setStaged(null);

    // Идёт заведение заявки в СНС: ответы на вопросы и 📎 — в заявку (#440).
    if (await handleInFlow(text, file)) return;
    // «Какие банки подключены к СНС», «а Сбербанк?», «подай заявку в …» — в обоих режимах.
    if (text && !file && await handleIntents(text)) return;

    // Брокер + вложение → пересчёт карточки по распознанной отчётности клиента.
    if (mode === "broker" && file) { await runBrokerRecalcByDoc(file); return; }

    // Брокер: запрос анализа клиента → показываем clientAnalysis из последнего подбора.
    if (mode === "broker" && text && isClientAnalyticsIntent(text)) {
      const ca = lastBrokerRef.current?.res.clientAnalysis;
      if (ca) pushText("assistant", "Анализ клиента (по последнему подбору):\n\n" + narrate(ca, ctx.current.amount || DEFAULT_LIMIT));
      else pushText("assistant", "Сначала подберём банки — полный анализ клиента я беру из подбора (нужен ИНН). Укажите параметры и напишите «подбери».");
      return;
    }

    // Брокер: РНТ в тексте → подтягиваем тендер (сумма обеспечения/закон/заказчик/ОКПД2).
    if (mode === "broker" && text) {
      const rnt = extractTenderNumber(text);
      if (rnt && rnt !== ctx.current.tenderNumber) {
        setBusy(true);
        try { await prefillFromTender(rnt); } finally { setBusy(false); }
      }
    }

    // Правило (как во встроенном чате «Брокера», #381): детерминированный разбор —
    // быстрый путь ТОЛЬКО для стандартных команд и параметров формы. Запрос вышел за
    // рамки скрипта (вопрос, неразобранное, падение разбора) → подхватывает ЛЛМ и
    // отвечает по данным ANYskin через наши read-only инструменты.
    let resp: Awaited<ReturnType<DeterministicAgent["turn"]>>;
    const before = ctxSig(ctx.current);
    try {
      resp = await agent.current.turn(text, file ? [file] : [], ctx.current);
    } catch (e) {
      // Разбор упал — не теряем запрос, отдаём ассистенту.
      console.error("agent.turn failed, fallback to assistant:", e);
      await askAssistant(text);
      return;
    }

    const hasRun = resp.actions.some((a) => a.type === "broker" || a.type === "analyze");
    const changed = ctxSig(resp.ctx) !== before;
    // Ни операции, ни распознанного параметра (или параметр, но со знаком вопроса) —
    // это не «кнопка», а вопрос: отвечает ЛЛМ. В режиме «Анализ» это правило — ЛЮБОЕ
    // сообщение без конкретного действия (без hasRun): там нет пошагового заполнения
    // формы заявки, как в брокере, где «10 млн»/«60 дней» без «?» законно остаются
    // параметрами — здесь это почти всегда продолжение диалога («а если выручку до
    // 100 млн?» без «?» на конце). Раньше такое сообщение детерминированный разбор
    // по ошибке принимал за «похоже на сумму — предложу новый анализ» и диалог
    // терял контекст.
    if (text && !hasRun && (mode === "analysis" || !changed || /[?？]/.test(text))) {
      // Разобранное в вопросе не теряем: параметры и ПРИКРЕПЛЁННЫЕ ФАЙЛЫ остаются в
      // контексте. Флаг «сводку показали» возвращаем как было — сводку мы не показывали,
      // и следующее «да» должно её получить, а не запускать подбор вслепую.
      ctx.current = { ...resp.ctx, brokerAsked: ctx.current.brokerAsked };
      // Ответ скрипта держим про запас: модель выключена (LLM_ENABLED) или недоступна —
      // пользователь получит прежнюю подсказку, а не голую ошибку.
      const scripted = resp.actions.find((a) => a.type === "reply") as { text: string } | undefined;
      await askAssistant(text, scripted?.text);
      return;
    }

    ctx.current = resp.ctx;
    for (const a of resp.actions) {
      if (a.type === "reply") pushText("assistant", a.text);
      else if (a.type === "broker") await runBroker(a);
      else await runAnalyze(a.inn, a.amount, a.files);
    }
  };

  // Умный ассистент: нестандартный запрос → бэкенд (ЛЛМ + tool-loop) → ответ по данным
  // ANYskin. Контекст даём по режиму: подбор (брокер) либо карточка анализа.
  const askAssistant = async (text: string, scripted?: string) => {
    setBusy(true); setAsking(true);
    try {
      const c = ctx.current;
      const context: Record<string, any> = {
        inn: c.inn, beneficiaryInn: c.beneficiaryInn,
        amount: c.amount, days: c.days, law: c.law, guaranteeType: c.guaranteeType,
        tenderNumber: c.tenderNumber,
        result: mode === "broker" ? (lastBrokerRef.current?.res || null) : (lastAnalysisRef.current || null),
      };
      const history = messages
        .filter((m) => m.kind === "text" || m.kind === "assistant")
        .slice(-6)
        .map((m) => ({ role: m.role, content: m.text }));
      const r = await api.brokerAssistant(text, { ...context, ...(seedContext || {}) },
                                          history, (seedProduct as any) || undefined);
      setMessages((m) => [...m, {
        id: nid(), role: "assistant", kind: "assistant",
        text: r.answer || "Не смог сформировать ответ.", question: text,
        toolCalls: r.toolCalls, ctx: { ...context, ...(seedContext || {}) }, accepted: null,
      }]);
    } catch (e: any) {
      // Модель недоступна/выключена → отдаём ответ скрипта (если он был), иначе причину.
      console.error("assistant failed:", e);
      pushText("assistant", scripted || `Не смог ответить: ${e?.message || e}`);
    } finally { setBusy(false); setAsking(false); }
  };

  // Оценка ответа ассистента → база принятых ответов (few-shot/кэш/мониторинг качества).
  const rateAnswer = async (m: Extract<Msg, { kind: "assistant" }>, verdict: "up" | "down") => {
    if (m.accepted) return;
    setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, accepted: verdict } as Msg : x)));
    try {
      await api.brokerAssistantAccept({ question: m.question, answer: m.text, context: m.ctx, toolCalls: m.toolCalls, verdict });
    } catch { /* тихо: оценка не критична */ }
  };

  // Пересчёт по рекомендованному балансу — новая «проекция» как сообщение чата.
  const handleRecalc = async (m: Extract<Msg, { kind: "result" }>) => {
    if (busy || !m.result.targetReport) return;
    const balance: Record<string, number> = {};
    m.result.targetReport.lines.forEach((l) => { balance[l.code] = l.target; });
    setBusy(true);
    try {
      const proj = await api.calculateWithBalance(m.result.company.inn, m.amount, balance);
      setMessages((x) => [...x, {
        id: nid(), role: "assistant", kind: "result", amount: m.amount, result: proj,
        text: "📈 Проекция по рекомендованному балансу — показатели при достижении целевых значений:\n\n" + narrate(proj, m.amount),
      }]);
    } catch (e: any) {
      pushText("assistant", `Не удалось пересчитать по целевому балансу: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const newAnalysis = () => {
    ctx.current = initialContext(mode);
    setStaged(null);
    setMessages([{ id: nid(), role: "assistant", kind: "text", text: agent.current.greeting(mode) }]);
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const hasResult = messages.some((m) => m.kind === "result" || m.kind === "broker");

  return (
    <main className={
      floating
        ? "px-3 pt-3 w-full h-full flex flex-col overflow-y-auto overscroll-contain"
        : "max-w-4xl mx-auto px-4 sm:px-5 pt-6 w-full flex flex-col min-h-[calc(100dvh-6rem-env(safe-area-inset-top))] md:min-h-[calc(100vh-140px)]"
    }>
      {!floating && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <LogoMark size={40} className="shrink-0 drop-shadow-sm" />
            <div>
              <h2 className="text-lg md:text-xl font-extrabold font-display text-slate-900 tracking-tight">AI агент</h2>
              <p className="text-[11px] text-slate-500">
                {mode === "broker" ? "Подбор банковской гарантии в режиме диалога" : "Экспресс-анализ контрагента в режиме диалога"}
              </p>
            </div>
          </div>
          <button onClick={onBack}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-display text-xs uppercase font-bold tracking-wider py-2 px-4 rounded-xl shadow-sm transition-colors cursor-pointer inline-flex items-center gap-1 shrink-0">
            <ChevronLeft className="w-4 h-4" /> На главную
          </button>
        </div>
      )}

      {/* Переключатель режимов агента */}
      <div className="mb-4 inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm self-start">
        {([["analysis", "Анализ клиента", Gauge], ["broker", "Брокер", Briefcase]] as const).map(([key, label, Icon]) => (
          <button key={key} onClick={() => switchMode(key)} disabled={busy}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer disabled:opacity-50 ${
              mode === key ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Лента сообщений */}
      <div className="flex-1 space-y-4 pb-4">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <LogoMark size={28} className="shrink-0 mr-2 mt-1" />
            )}
            <div className={
              m.role === "user"
                ? "max-w-[85%] bg-indigo-600 text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm shadow-sm"
                : `${m.kind === "result" ? "w-full" : "max-w-[85%]"} bg-white border border-slate-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm`
            }>
              {m.kind === "result" ? (
                <ResultBubble m={m} onRecalc={handleRecalc} recalcBusy={busy} onAskAssistant={(q) => send(q)} />
              ) : m.kind === "broker" ? (
                <BrokerResultBubble m={m} disabled={busy} onSubmit={(o) => { if (!busy) void startSns(o); }}
                  onSubmitMany={(list) => { if (!busy) void submitChosen(list); }} />
              ) : m.kind === "snspick" ? (
                <>
                  <AssistantText text={m.text} className="text-sm text-slate-700 leading-relaxed mb-2" />
                  <SnsOffersBlock block={{ state: "ok", offers: m.offers }} title="Выберите банки для подачи в СНС" disabled={busy}
                    onSubmit={(o) => { void startSns(o); }} onSubmitMany={(list) => { void submitChosen(list); }} />
                </>
              ) : m.kind === "actions" ? (
                <>
                  <AssistantText text={m.text} className="text-sm text-slate-700 leading-relaxed" />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.actions.map((a, i) => (
                      <button key={i} onClick={a.run} disabled={m.done || busy}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg cursor-pointer disabled:opacity-40 disabled:cursor-default ${a.primary
                          ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                          : "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"}`}>{a.label}</button>
                    ))}
                  </div>
                </>
              ) : m.kind === "assistant" ? (
                <>
                  <AssistantText text={m.text} className="text-sm text-slate-700 leading-relaxed" />
                  <div className="mt-2 flex items-center gap-3 text-slate-400">
                    <button onClick={() => rateAnswer(m, "up")} title="Полезно" disabled={!!m.accepted}
                      className={`text-sm cursor-pointer hover:text-emerald-600 ${m.accepted === "up" ? "text-emerald-600" : ""} ${m.accepted ? "cursor-default" : ""}`}>👍</button>
                    <button onClick={() => rateAnswer(m, "down")} title="Неполезно" disabled={!!m.accepted}
                      className={`text-sm cursor-pointer hover:text-rose-600 ${m.accepted === "down" ? "text-rose-600" : ""} ${m.accepted ? "cursor-default" : ""}`}>👎</button>
                    {m.accepted === "up" && <span className="text-[11px] text-emerald-600 font-medium">сохранено в базу принятых</span>}
                    {m.accepted === "down" && <span className="text-[11px] text-rose-500 font-medium">спасибо, учту</span>}
                  </div>
                </>
              ) : (
                <>
                  {m.role === "user"
                    ? <p className="text-sm whitespace-pre-line leading-relaxed">{m.text}</p>
                    : <AssistantText text={m.text} className="text-sm leading-relaxed text-slate-700" />}
                  {m.files && m.files.length > 0 && (
                    <div className="mt-1.5 text-[11px] opacity-80">📎 {m.files.join(", ")}</div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {busy && (
          <div className="flex justify-start">
            <LogoMark size={28} className="shrink-0 mr-2 mt-1" />
            <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm inline-flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
              {busyNote || (asking ? "Думаю над ответом по данным ANYskin…"
                : ocrPending ? "Распознаю скан через OCR — это может занять до минуты…"
                : mode === "broker" ? "Подбираю банки по реестру…" : "Анализирую контрагента…")}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Быстрые действия */}
      {/* Быстрые кнопки шлют «да» — при сборе заявки в СНС это «отправить», прячем (#440). */}
      {!busy && !snsOn && (
        <div className="flex flex-wrap gap-2 mb-2">
          {mode === "analysis" && ctx.current.inn && (
            <button onClick={() => send("да")}
              className="text-xs font-bold bg-emerald-50 border border-emerald-100 text-emerald-800 hover:bg-emerald-100 rounded-full px-3 py-1.5 cursor-pointer">
              🚀 Анализировать {ctx.current.inn}
            </button>
          )}
          {mode === "broker" && ctx.current.amount != null && ctx.current.days != null && (
            <button onClick={() => send("да")}
              className="text-xs font-bold bg-emerald-50 border border-emerald-100 text-emerald-800 hover:bg-emerald-100 rounded-full px-3 py-1.5 cursor-pointer">
              🏦 Подобрать гарантию
            </button>
          )}
          {hasResult && (
            <button onClick={newAnalysis}
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full px-3 py-1.5 cursor-pointer">
              <RefreshCw className="w-3.5 h-3.5" /> {mode === "broker" ? "Новый подбор" : "Новый анализ"}
            </button>
          )}
        </div>
      )}

      {/* Композер */}
      <div className="sticky bottom-0 bg-gradient-to-t from-slate-50 via-slate-50 to-transparent dark:from-slate-900 dark:via-slate-900 pt-2 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
        {staged && (
          <div className="mb-2 inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-600">
            📎 {staged.name}
            <button onClick={() => setStaged(null)} aria-label="Убрать файл" className="grid place-items-center w-8 h-8 -my-1.5 text-slate-400 hover:text-rose-600 cursor-pointer"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        <div className="flex items-end gap-2 bg-white border border-slate-200 focus-within:border-indigo-400 rounded-2xl p-2 shadow-sm">
          <input ref={fileRef} type="file" accept={snsOn ? api.SNS_ACCEPT : ACCEPT} className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) setStaged(f); if (fileRef.current) fileRef.current.value = ""; }} />
          <button onClick={() => fileRef.current?.click()} disabled={busy}
            title={mode === "broker" ? "Прикрепить отчётность клиента — пересчитаю подбор по ней (PDF/Excel/скан)" : "Прикрепить отчётность (PDF/Excel/скан)"}
            aria-label="Прикрепить файл"
            className="shrink-0 grid place-items-center w-11 h-11 -m-1 text-slate-400 hover:text-indigo-600 disabled:opacity-40 cursor-pointer">
            <Paperclip className="w-5 h-5" />
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            disabled={busy}
            placeholder={mobile ? (mode === "broker" ? "Сумма, срок, закон, ИНН…" : "ИНН и сумма…")
              : (mode === "broker" ? "Например: 5 млн на 365 дней, 44-ФЗ, ИНН 7707083893…" : "Например: 7707083893 на 50 млн…")}
            enterKeyHint="send" aria-label="Сообщение" autoCapitalize="sentences"
            className="flex-1 resize-none bg-transparent outline-none text-sm py-1.5 min-h-11 md:min-h-0 max-h-32 disabled:opacity-50"
          />
          <button onClick={() => send()} disabled={busy || (!input.trim() && !staged)} aria-label="Отправить"
            className="shrink-0 grid place-items-center w-11 h-11 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-xl transition-colors cursor-pointer">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="hidden sm:block text-[10px] text-slate-400 text-center mt-1.5">
          Данные берутся из открытых федеральных ресурсов; при прикреплении файла — из вашей отчётности.
        </p>
      </div>
    </main>
  );
}
